import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../hooks/useUserData';
// Removed Redux auth selectors - now using useAuth() hook
import { subscribeToNotifications, markNotificationComplete } from '../../services/notificationService';
import './NotificationBell.css';

const modernStyles = {
  iconButton: {
    color: '#00FFD0',
    '&:hover': {
      background: 'rgba(0, 255, 208, 0.1)',
    },
  },
  badge: {
    '& .MuiBadge-badge': {
      background: 'linear-gradient(135deg, #00FFD0 0%, #00BFA5 100%)',
      color: '#0A192F',
    },
  },
  menu: {
    '& .MuiPaper-root': {
      background: 'linear-gradient(135deg, rgba(16, 20, 24, 0.95) 0%, rgba(0, 48, 46, 0.9) 100%)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(0, 255, 208, 0.1)',
      boxShadow: '0 4px 30px rgba(0, 255, 208, 0.1)',
      color: '#fff',
    },
  },
  notificationItem: {
    borderBottom: '1px solid rgba(0, 255, 208, 0.1)',
    '&:hover': {
      background: 'rgba(0, 255, 208, 0.05)',
    },
    '&.action': {
      borderLeft: '4px solid #00FFD0',
    },
    '&.view': {
      borderLeft: '4px solid #00BFA5',
    },
  },
};

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const { user: currentUser } = useUserData();
  const navigate = useNavigate();
  const hasFetchedInitial = useRef(false);
  const fetchTimeoutRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Memoize userId to avoid unnecessary effect reruns
  const userId = useMemo(() => currentUser?.uid || currentUser?.id, [currentUser]);

  // Debounced fetch function - uses ref for loading state to avoid dependency issues
  const debouncedFetchNotifications = useCallback((userId) => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    fetchTimeoutRef.current = setTimeout(async () => {
      // Use ref to check loading state to avoid stale closure issues
      if (isLoadingRef.current) return;
      
      try {
        isLoadingRef.current = true;
        const { getUserNotifications } = await import('../../services/notificationService');
        const initialNotifications = await getUserNotifications(userId, { unreadOnly: true });
        if (Array.isArray(initialNotifications)) {
          setNotifications(initialNotifications);
        } else {
          console.warn('NotificationBell: Received invalid initial notifications data:', initialNotifications);
          setNotifications([]);
        }
        hasFetchedInitial.current = true;
      } catch (error) {
        console.error('Error fetching initial notifications:', error);
        setNotifications([]);
      } finally {
        isLoadingRef.current = false;
      }
    }, 300); // 300ms debounce
  }, []); // Empty deps - uses refs for mutable state

  useEffect(() => {
    if (!userId) {
      return;
    }

    // Only fetch initial notifications once per user session
    if (!hasFetchedInitial.current) {
      // Use debounced fetch
      debouncedFetchNotifications(userId);
    }

    // Subscribe to notifications using service layer
    const cleanup = subscribeToNotifications(userId, (update) => {
      if (update.notifications && Array.isArray(update.notifications)) {
        setNotifications(update.notifications);
      } else {
        console.warn('NotificationBell: Received invalid notifications data:', update);
        setNotifications([]);
      }
    });

    return () => {
      cleanup();
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [userId, debouncedFetchNotifications]);

  // Reset the fetch flag when userId changes
  useEffect(() => {
    hasFetchedInitial.current = false;
  }, [userId]);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNotificationClick = useCallback((notification) => {
    if (notification.actionLink) {
      navigate(notification.actionLink);
    }
    handleClose();
  }, [navigate, handleClose]);

  const handleMarkComplete = useCallback(async (notificationId, event) => {
    event.stopPropagation();
    try {
      await markNotificationComplete(notificationId);
    } catch (error) {
      console.error('Error marking notification as complete:', error);
    }
  }, []);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  }, []);

  // Memoize the badge content to prevent unnecessary re-renders
  const badgeContent = useMemo(() => Array.isArray(notifications) ? notifications.length : 0, [notifications]);

  // Memoize the menu open state
  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);

  return (
    <div className="notification-bell">
      <IconButton
        sx={modernStyles.iconButton}
        onClick={handleClick}
        className="notification-button"
      >
        <Badge 
          badgeContent={badgeContent} 
          sx={modernStyles.badge}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        className="notification-menu"
        sx={modernStyles.menu}
        PaperProps={{
          className: 'notification-menu-paper'
        }}
      >
        <Box className="notification-header" sx={{ color: '#00FFD0' }}>
          <Typography variant="h6">Notifications</Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(0, 255, 208, 0.1)' }} />
        <List className="notification-list">
          {!Array.isArray(notifications) || notifications.length === 0 ? (
            <ListItem>
              <ListItemText 
                primary="No new notifications" 
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              />
            </ListItem>
          ) : (
            notifications.map((notification) => (
              <ListItem
                key={notification.id}
                button
                onClick={() => handleNotificationClick(notification)}
                className={`notification-item ${notification.type}`}
              >
                <ListItemText
                  primary={notification.title}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="textPrimary">
                        {notification.message}
                      </Typography>
                      <Typography component="span" variant="caption" color="textSecondary">
                        {formatDate(notification.createdAt)}
                      </Typography>
                    </>
                  }
                />
                <Button
                  className="mark-complete-button"
                  onClick={(e) => handleMarkComplete(notification.id, e)}
                  color="primary"
                  size="small"
                >
                  Mark Complete
                </Button>
              </ListItem>
            ))
          )}
        </List>
      </Menu>
    </div>
  );
};

export default React.memo(NotificationBell); 